import React, { useEffect, useState } from "react";
import {
  PlusIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CalculatorIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  useQuotesPage,
  useCreateQuote,
  useUpdateQuote,
  useDeleteQuote,
  useConvertQuote,
  toQuoteRequest,
} from "../../hooks/useQuotes";
import { useSalesOrderSummary } from "../../hooks/useSales";
import {
  SalesOrder,
  CreateSalesOrderRequest,
  SalesOrderSortField,
} from "../../types/sales.types";
import { Quote, QuoteConversionResult } from "../../types/quote.types";
import QuoteConversionModal from "../common/QuoteConversionModal";
import CreateQuoteModal from "./CreateQuoteModal";
import ViewQuoteModal from "./ViewQuoteModal";
import EditQuoteModal from "./EditQuoteModal";
import { Pagination } from "../ui/Pagination";
import { ConfirmDialog, getApiErrorMessage } from "../ui/FormControls";
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SORT_OPTIONS: { value: SalesOrderSortField; label: string }[] = [
  { value: "createdAt", label: "Date created" },
  { value: "orderDate", label: "Quote date" },
  { value: "expectedDeliveryDate", label: "Expected delivery" },
  { value: "grandTotal", label: "Amount" },
];

// The conversion dialog works with the Quote shape
const salesOrderToQuote = (order: SalesOrder): Quote => {
  return {
    id: order.id,
    orderNumber: order.orderNumber,
    customerId: order.customer?.id || order.customerId || "",
    customer: {
      id: order.customer?.id || order.customerId || "",
      name: order.customer?.name || "Unknown",
      code: order.customer?.code || "",
      email: order.customer?.email,
      phone: order.customer?.phone,
    },
    orderDate: order.orderDate,
    expectedDeliveryDate: order.expectedDeliveryDate,
    status: "quote",
    totalAmount: order.totalAmount,
    taxAmount: order.taxAmount,
    discountAmount: order.discountAmount,
    grandTotal: order.grandTotal,
    notes: order.notes,
    shippingAddress: order.shippingAddress,
    deliveryTerms: order.deliveryTerms,
    paymentTerms: order.paymentTerms,
    shippingMethod: order.shippingMethod,
    deliveryLocation: order.deliveryLocation,
    specialInstructions: order.specialInstructions,
    promisedDate: order.promisedDate,
    items: order.items.map((item) => ({
      id: item.id || "",
      productId: item.productId,
      productName: item.productName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountRate: item.discountRate || 0,
      discountAmount: item.discountAmount || 0,
      taxRate: item.taxRate || 0,
      taxAmount: item.taxAmount || 0,
      totalAmount: item.totalAmount,
      totalPrice: item.totalPrice,
    })),
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
  };
};

const formatNumber = (value: number) => Number(value || 0).toLocaleString("en-IN");

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(amount || 0));

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const isExpired = (quote: SalesOrder) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(quote.expectedDeliveryDate) < today;
};

const QuoteManagement: React.FC = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<SalesOrderSortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [selectedQuote, setSelectedQuote] = useState<SalesOrder | null>(null);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SalesOrder | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: quotesPage, isLoading, isFetching, error } = useQuotesPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  });
  const { data: summary, isLoading: isSummaryLoading } = useSalesOrderSummary();
  const createMutation = useCreateQuote();
  const updateMutation = useUpdateQuote();
  const deleteMutation = useDeleteQuote();
  const convertMutation = useConvertQuote();

  const quotes = quotesPage?.data ?? [];
  // The quote that is open, as the list has it now: marking it as sent or revising it shows at once
  const openQuote = quotes.find((quote) => quote.id === selectedQuote?.id) ?? selectedQuote;
  const total = quotesPage?.total ?? 0;
  const totalPages = quotesPage?.totalPages ?? 1;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (quotesPage && page > 1 && page > quotesPage.totalPages) {
      setPage(Math.max(1, quotesPage.totalPages));
    }
  }, [quotesPage, page]);

  const handleCreateQuote = async (data: CreateSalesOrderRequest) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success("Quote created successfully");
      setCreateModalOpen(false);
      setPage(1);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not create the quote"));
      throw err;
    }
  };

  const handleUpdateQuote = async (data: CreateSalesOrderRequest) => {
    if (!selectedQuote) return;
    try {
      await updateMutation.mutateAsync({ id: selectedQuote.id, data });
      toast.success("Quote updated successfully");
      setEditModalOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not update the quote"));
      throw err;
    }
  };

  const handleDuplicateQuote = async (quote: SalesOrder) => {
    try {
      const copy = await createMutation.mutateAsync(toQuoteRequest(quote));
      toast.success(`Quote duplicated as ${copy.orderNumber}`);
      setPage(1);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not duplicate the quote"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success("Quote deleted successfully");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not delete the quote"));
    }
  };

  const handleConfirmConversion = async (): Promise<QuoteConversionResult> => {
    if (!selectedQuote) {
      return { success: false, message: "No quote selected" };
    }
    try {
      const order = await convertMutation.mutateAsync(selectedQuote.id);
      toast.success("Quote converted to order");
      return {
        success: true,
        orderId: order.id,
        orderNumber: order.orderNumber,
        message: "It now appears under Sales Orders, waiting to be confirmed.",
      };
    } catch (err) {
      return { success: false, message: getApiErrorMessage(err, "Could not convert the quote") };
    }
  };

  const openView = (quote: SalesOrder) => {
    setSelectedQuote(quote);
    setViewModalOpen(true);
  };

  const openEdit = (quote: SalesOrder) => {
    setSelectedQuote(quote);
    setEditModalOpen(true);
  };

  const openConvert = (quote: SalesOrder) => {
    setSelectedQuote(quote);
    setConversionModalOpen(true);
  };

  const hasFilters = !!search;
  const quoteCount = summary?.quotes ?? 0;
  const quoteValue = summary?.quoteValue ?? 0;

  const stats = [
    { label: "Open Quotes", value: formatNumber(quoteCount), icon: DocumentTextIcon, tone: "text-indigo-600 bg-indigo-50" },
    { label: "Total Quote Value", value: formatCurrency(quoteValue), icon: BanknotesIcon, tone: "text-green-600 bg-green-50" },
    { label: "Average Quote Value", value: formatCurrency(quoteCount > 0 ? quoteValue / quoteCount : 0), icon: CalculatorIcon, tone: "text-blue-600 bg-blue-50" },
  ];

  const selectClass =
    "h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";
  const thClass = "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
  const iconButtonClass = "p-1.5 rounded disabled:opacity-50";

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading quotes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Quote Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage sales quotes and convert them to orders
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Quote
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? "—" : stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search and sort */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by quote number or customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Sort by"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SalesOrderSortField);
                setPage(1);
              }}
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Sort: {option.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Sort direction"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as "ASC" | "DESC");
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="DESC">{sortBy === "grandTotal" ? "Highest first" : "Newest first"}</option>
              <option value="ASC">{sortBy === "grandTotal" ? "Lowest first" : "Oldest first"}</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Quotes table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Quote</th>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Quote Date</th>
                <th className={thClass}>Expected Delivery</th>
                <th className={thClass}>Items</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? "opacity-60" : ""}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={7}>
                    Loading quotes...
                  </td>
                </tr>
              ) : quotes.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={7}>
                    <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No quotes found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? "Try changing or clearing the search."
                        : "Create a quote to send a customer a price before they order."}
                    </p>
                  </td>
                </tr>
              ) : (
                quotes.map((quote) => {
                  const expired = isExpired(quote);
                  return (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {quote.orderNumber}
                          {quote.quoteRevision ? ` Rev ${quote.quoteRevision}` : ''}
                          {quote.quoteSentAt && <span className="ml-2 rounded bg-green-50 px-1.5 py-0.5 font-sans text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">Sent</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {quote.customer?.name || "Unknown Customer"}
                        </div>
                        {quote.customer?.code && <div className="text-gray-500">{quote.customer.code}</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatDate(quote.orderDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className={expired ? "font-medium text-red-600" : "text-gray-700"}>
                          {formatDate(quote.expectedDeliveryDate)}
                        </div>
                        {expired && <div className="text-xs text-red-600">Date has passed</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div>
                          {quote.items.length} {quote.items.length === 1 ? "item" : "items"}
                        </div>
                        {quote.items.length > 0 && (
                          <div className="max-w-xs truncate text-gray-500">
                            {quote.items
                              .slice(0, 2)
                              .map((item) => item.productName)
                              .join(", ")}
                            {quote.items.length > 2 && `, +${quote.items.length - 2} more`}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                        {formatCurrency(quote.grandTotal)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => openConvert(quote)}
                            className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                            title="Convert to Order"
                          >
                            <ArrowRightIcon className="h-3.5 w-3.5 mr-1" />
                            Convert
                          </button>
                          <button
                            onClick={() => openView(quote)}
                            className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                            title="View Quote"
                            aria-label="View Quote"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEdit(quote)}
                            className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                            title="Edit Quote"
                            aria-label="Edit Quote"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDuplicateQuote(quote)}
                            disabled={createMutation.isPending}
                            className={`${iconButtonClass} text-blue-600 hover:bg-blue-50 hover:text-blue-900`}
                            title="Duplicate Quote"
                            aria-label="Duplicate Quote"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(quote)}
                            className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                            title="Delete Quote"
                            aria-label="Delete Quote"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} quotes
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Create Quote Modal */}
      <CreateQuoteModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateQuote}
        isLoading={createMutation.isPending}
      />

      {/* View Quote Modal */}
      <ViewQuoteModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        quote={openQuote || undefined}
        onEdit={(quote) => {
          setViewModalOpen(false);
          openEdit(quote);
        }}
        onConvert={(quote) => {
          setViewModalOpen(false);
          openConvert(quote);
        }}
      />

      {/* Edit Quote Modal */}
      <EditQuoteModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateQuote}
        quote={openQuote || undefined}
        isLoading={updateMutation.isPending}
      />

      {/* Quote Conversion Modal */}
      <QuoteConversionModal
        isOpen={conversionModalOpen}
        onClose={() => setConversionModalOpen(false)}
        quote={selectedQuote ? salesOrderToQuote(selectedQuote) : undefined}
        onConfirm={handleConfirmConversion}
        onCancel={() => setConversionModalOpen(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Quote"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.orderNumber}</span> for{" "}
            {deleteTarget?.customer?.name || "Unknown Customer"} will be permanently deleted. This cannot
            be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default QuoteManagement;
